/* Read-side helpers shared by the queue, the ticket and the home screens.
 *
 * Kept out of the store so the same functions work unchanged when the store
 * starts returning rows from Snowflake instead of localStorage.
 */

import { slaStanding, workingMinutesBetween } from '../demo/sla.js';

/**
 * Is this ticket's owner the person looking at the screen?
 * A demo ticket's owner is a demo staff id. A filed ticket's owner is a sign-in address (S06),
 * so it is compared with the signed-in address, ignoring case.
 */
export function ownedBy(ticket, { meStaffId, email }) {
  if (!ticket || !ticket.assignee) return false;
  if (ticket.fromServer) return String(ticket.assignee).toLowerCase() === String(email || '').trim().toLowerCase();
  return ticket.assignee === meStaffId;
}

/**
 * Where a ticket stands against both its targets.
 * Returns null when reference data has not loaded yet, so callers render the
 * ticket rather than an empty space.
 */
export function slaFor(ticket, reference, now = new Date()) {
  if (!ticket || !reference) return null;
  const policy = reference.sla.find((p) => p.priority === ticket.priority);
  if (!policy) return null;

  const cal = reference.calendar;
  if (ticket.fromServer && ticket.responseDueAt && ticket.resolveDueAt) {
    return { policy, ...serverSla(ticket, policy, cal, now) };
  }
  const shared = {
    raisedAt: ticket.raisedAt,
    pausedMins: ticket.pausedMins || 0,
    cal,
    aroundClock: policy.aroundClock,
  };

  return {
    policy,
    response: slaStanding({ ...shared, stoppedAt: responseStop(ticket), targetMins: policy.responseMins }),
    resolve: slaStanding({ ...shared, stoppedAt: ticket.resolvedAt, targetMins: policy.resolveMins }),
  };
}

/* The response target stops at IT's first reply - or at the resolve, when a ticket was resolved
 * before IT ever replied (Grant, 2026-10-23); otherwise it would read "missed" for ever. The server
 * stamps the first-reply time at such a resolve too (ticketCommand.js), so this only matters for a
 * ticket resolved before that, and for demo tickets. */
const isDone = (t) => t.status === 'resolved' || t.status === 'closed';
const responseStop = (t) => t.firstResponseAt || (isDone(t) ? t.resolvedAt : null) || null;

/* A FILED ticket's clocks, from the due times the server keeps. The server moves them when a hold
 * ends or the priority changes (P1 by every minute, the rest by working time), so nothing here
 * redoes that arithmetic: the time left is simply the time from now to the due time. While a hold
 * is open the clock is frozen where the hold began - the server moves the due time when it ends.
 * Once IT has answered, the response target is settled and stops at the first answer. */
function serverSla(ticket, policy, cal, now) {
  const frozenAt = ticket.status === 'waiting' && ticket.waitingSince ? new Date(ticket.waitingSince) : null;
  const one = (dueAt, stoppedAt, targetMins) => {
    const due = new Date(dueAt);
    const end = stoppedAt ? new Date(stoppedAt) : frozenAt || now;
    const remaining = end <= due
      ? workingMinutesBetween(end, due, cal, policy.aroundClock)
      : -workingMinutesBetween(due, end, cal, policy.aroundClock);
    let state;
    if (stoppedAt) state = remaining >= 0 ? 'met' : 'breached';
    else if (remaining < 0) state = 'breached';
    else if (remaining <= targetMins * 0.2) state = 'at_risk';
    else state = 'ok';
    return { state, usedMins: Math.max(0, Math.round(targetMins - remaining)), targetMins, remainingMins: Math.round(remaining) };
  };
  return {
    response: one(ticket.responseDueAt, responseStop(ticket), policy.responseMins),
    resolve: one(ticket.resolveDueAt, ticket.status === 'resolved' || ticket.status === 'closed' ? ticket.resolvedAt : null, policy.resolveMins),
  };
}

/** The single worst thing true about a ticket's clocks, for a one-chip summary. */
export function worstSla(sla) {
  if (!sla) return null;
  const order = { breached: 3, at_risk: 2, ok: 1, met: 0 };
  return order[sla.response.state] >= order[sla.resolve.state] ? sla.response : sla.resolve;
}

const OPEN = ['new', 'in_progress', 'waiting'];

export function isOpen(ticket) {
  return OPEN.includes(ticket.status);
}

/* Where a finished ticket stands for its requester (S07b part 3b-2), from what the server said:
 *   'open'        not resolved or closed;
 *   'reopenable'  resolved, inside the reopen window: "Still not fixed" is offered;
 *   'resolved'    resolved, but no window came back (the server could not say, or it cannot reopen
 *                 tickets yet): neither button is offered, and a reply still reaches IT;
 *   'closed'      closed, or resolved and past the window: a reply is refused, and the page offers
 *                 "Start a new ticket".
 * The server decides again on every reply; this only picks what to offer. */
export function reopenState(ticket, now = new Date()) {
  if (ticket.status === 'closed') return 'closed';
  if (ticket.status !== 'resolved') return 'open';
  const until = ticket.reopenUntil ? new Date(ticket.reopenUntil).getTime() : NaN;
  if (Number.isNaN(until)) return 'resolved';
  return now.getTime() < until ? 'reopenable' : 'closed';
}

/* Where "Start a new ticket" goes: the same form, as a follow-up of this ticket. Only a filed ticket
 * can be followed up; a demo ticket just opens the same form. `for` is set only when the reader
 * raised the old ticket for someone else (the server sends raisedForUpn only then). */
export function followUpHref(ticket) {
  const q = new URLSearchParams();
  if (ticket.requestTypeId) q.set('type', ticket.requestTypeId);
  if (ticket.fromServer) {
    q.set('followUpOf', ticket.number);
    if (ticket.raisedForUpn) q.set('for', ticket.raisedForUpn);
  }
  return `/new?${q.toString()}`;
}
